/**
 * The quick-search palette's brain: what matches, and in what order. Pure —
 * no DOM, no Firebase, no timers — so every ranking rule is testable with a
 * plain array. (ARCHITECTURE.md §3.)
 *
 * ── Why ranking is here and not in the palette ────────────────────────────
 *
 * Legacy's search is `summary.search.includes(needle)` and nothing more
 * (app.html:5828 via `trip-summary.js#matchesSearch`): typing "ma" surfaces
 * "Emma Wilson" above "Maya Patel" if Emma happens to sort first. Fine for
 * one filtered list; wrong for a palette, where the advisor reads only the
 * top row and presses Enter. The two rules that make the top row the right
 * row:
 *
 *   1. A PREFIX hit beats a substring hit — "ma" is someone starting to type
 *      "Maya", not someone hunting for the middle of "Emma".
 *   2. A NAME hit beats a destination hit — "paris" should list the client
 *      called Paris Hilton above the forty trips going to France.
 *
 * ── One item shape for three collections ──────────────────────────────────
 *
 * Trips come from the summary index, clients and leads from their own
 * collections, and each spells its fields differently. The builders below
 * flatten all three into `SearchItem` at fetch time, so the ranker never
 * knows Firestore exists — and so lowercasing happens once per fetch, not
 * once per keystroke per record (the same reasoning as the index's
 * pre-lowercased `search` field, `trip-summary.js:168`).
 */

/** @typedef {'trip'|'client'|'lead'} SearchKind */

/**
 * @typedef {Object} SearchItem
 * @property {SearchKind} kind
 * @property {string} id
 * @property {string} title - what the row shows, e.g. the client's name
 * @property {string} detail - the second column: destination, email
 * @property {string} name - lowercased primary match text (scores high)
 * @property {string} rest - lowercased secondary match text (scores low)
 */

/**
 * Eight per group. Enough that the wanted row is on screen without arrowing,
 * few enough that three groups still fit an overlay on a laptop.
 */
export const GROUP_CAP = 8

/** @param {any} value @returns {string} */
const text = (value) => String(value == null ? '' : value).trim()

/**
 * The query as lowercase words. Every word must match somewhere for an item
 * to appear — "jessica bali" narrows, it does not widen.
 *
 * @param {unknown} query
 * @returns {string[]}
 */
export function tokenize(query) {
  return text(query).toLowerCase().split(/\s+/).filter(Boolean)
}

/**
 * A trip, from its index summary (`lib/trip-summary.js#tripSummary`). The
 * summary's `search` blob already carries name + destination + supplier,
 * pre-lowercased; it becomes `rest` so "royal caribbean" still finds the
 * trip, just below anything named Royal.
 *
 * @param {any} summary
 * @returns {SearchItem}
 */
export function tripItem(summary) {
  const s = summary || {}
  const title = text(s.clientName) || 'Untitled trip'
  const detail = text(s.destination)
  return {
    kind: 'trip',
    id: text(s.id),
    title,
    detail,
    name: title.toLowerCase(),
    rest: text(s.search || detail).toLowerCase(),
  }
}

/**
 * A client, from `users/{uid}/clients`. Email is secondary on purpose:
 * "rob@" should find Robert, but "rob" finding him by NAME must outrank it.
 *
 * @param {any} doc - `{id, ...data}`, as `readCollection` returns
 * @returns {SearchItem}
 */
export function clientItem(doc) {
  const c = doc || {}
  const title = text(c.name) || 'Unnamed client'
  const detail = text(c.email)
  return {
    kind: 'client',
    id: text(c.id),
    title,
    detail,
    name: title.toLowerCase(),
    rest: detail.toLowerCase(),
  }
}

/**
 * A lead, from `forms/{uid}/leads`. Same "Unknown" default the leads screen
 * uses (`leads-model.js#toLead`) — these fields were typed by strangers.
 *
 * @param {any} doc
 * @returns {SearchItem}
 */
export function leadItem(doc) {
  const l = doc || {}
  const title = text(l.name) || 'Unknown'
  const detail = text(l.destination) || text(l.email)
  return {
    kind: 'lead',
    id: text(l.id),
    title,
    detail,
    name: title.toLowerCase(),
    rest: `${text(l.email)} ${text(l.destination)}`.toLowerCase().trim(),
  }
}

/** @param {string} haystack @param {string} token @returns {boolean} */
const wordStarts = (haystack, token) =>
  haystack.split(/\s+/).some((word) => word.startsWith(token))

/**
 * One token against one item. The steps are deliberately far apart so that
 * summing several tokens cannot let three weak hits outrank one strong one:
 * two substring hits (60+60) still lose to two word-prefix hits (80+80).
 *
 * @param {SearchItem} item @param {string} token @returns {number}
 */
function tokenScore(item, token) {
  if (item.name.startsWith(token)) return 100
  if (wordStarts(item.name, token)) return 80 // "wil" → "Jessica Williams"
  if (item.name.includes(token)) return 60
  if (wordStarts(item.rest, token)) return 30
  if (item.rest.includes(token)) return 10
  return 0
}

/**
 * The whole query against one item: 0 unless EVERY token lands.
 *
 * @param {SearchItem} item
 * @param {string[]} tokens
 * @returns {number}
 */
export function scoreItem(item, tokens) {
  let total = 0
  for (const token of tokens) {
    const hit = tokenScore(item, token)
    if (hit === 0) return 0
    total += hit
  }
  return total
}

/**
 * One group's results, best first, capped.
 *
 * Ties break on title so the order is stable across renders — a list that
 * reshuffles identical scores under a moving selection is unusable by arrow
 * key. An empty query returns nothing: the palette's empty state is a
 * prompt, not a dump of every record.
 *
 * @param {SearchItem[]} items
 * @param {unknown} query
 * @param {number} [cap]
 * @returns {SearchItem[]}
 */
export function rankMatches(items, query, cap = GROUP_CAP) {
  const tokens = tokenize(query)
  if (!tokens.length) return []
  return (items || [])
    .map((item) => ({ item, score: scoreItem(item, tokens) }))
    .filter((scored) => scored.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.item.title.localeCompare(b.item.title) ||
        a.item.id.localeCompare(b.item.id)
    )
    .slice(0, Math.max(0, cap))
    .map((scored) => scored.item)
}

/**
 * @typedef {Object} SearchGroup
 * @property {SearchKind} kind
 * @property {string} label
 * @property {SearchItem[]} items
 */

/**
 * All three groups for one query, empty groups dropped. Trips first because
 * the trip editor is where an advisor spends the day; the other two follow
 * the hamburger menu's order.
 *
 * @param {{trips?: SearchItem[], clients?: SearchItem[], leads?: SearchItem[]}|null} data
 * @param {unknown} query
 * @param {number} [cap]
 * @returns {SearchGroup[]}
 */
export function searchGroups(data, query, cap = GROUP_CAP) {
  const d = data || {}
  return [
    { kind: /** @type {SearchKind} */ ('trip'), label: 'Trips', items: rankMatches(d.trips || [], query, cap) },
    { kind: /** @type {SearchKind} */ ('client'), label: 'Clients', items: rankMatches(d.clients || [], query, cap) },
    { kind: /** @type {SearchKind} */ ('lead'), label: 'Leads', items: rankMatches(d.leads || [], query, cap) },
  ].filter((group) => group.items.length > 0)
}
